import { BookingStatus } from '@/types/booking-management.types';

/**
 * Dashboard KPI cards that open the bookings list pre-filtered to the records the card's count is based on.
 * The filter values mirror the operator dashboard queries in the backend (harbor-operator-dashboard.service).
 */
export type BookingDrillDownView =
  | 'needs-attention'
  | 'upcoming-stays'
  | 'active-bookings';

export interface BookingDrillDown {
  label: string;
  status: number[];
  /** Departure (check-out) date on or after today in each harbour's own time zone. */
  departureFromToday?: boolean;
}

export const BOOKING_DRILL_DOWNS: Record<
  BookingDrillDownView,
  BookingDrillDown
> = {
  'needs-attention': {
    label: 'Needs attention',
    status: [BookingStatus.PENDING, BookingStatus.PENDING_CONFIRMATION],
  },
  'upcoming-stays': {
    label: 'Upcoming and current stays',
    status: [BookingStatus.CONFIRMED],
    departureFromToday: true,
  },
  'active-bookings': {
    label: 'Active bookings',
    status: [
      BookingStatus.PENDING,
      BookingStatus.PENDING_CONFIRMATION,
      BookingStatus.CONFIRMED,
    ],
  },
};

export const isBookingDrillDownView = (
  value: string | null
): value is BookingDrillDownView =>
  value !== null &&
  Object.prototype.hasOwnProperty.call(BOOKING_DRILL_DOWNS, value);

/**
 * Backend list params for a drill-down. The list is limited to the dashboard's own harbour scope and includes
 * unpaid drafts, so it holds exactly the records the card's count is based on (any role, incl. Platform Admin).
 */
export const getBookingDrillDownParams = (view: BookingDrillDownView) => {
  const drillDown = BOOKING_DRILL_DOWNS[view];

  return {
    status: drillDown.status,
    departure_from: drillDown.departureFromToday
      ? ('today' as const)
      : undefined,
    include_unpaid_drafts: true,
    dashboard_scope: true,
  };
};

/** Mediation cases that count as an open "Berth Conflict" on the dashboard (comma-separated, as the API expects). */
export const BERTH_CONFLICT_STATUSES =
  'OPEN,AWAITING_MANAGER,AWAITING_BOATER_B';

/** Route of the list a Berth Conflicts card opens. */
export const BERTH_CONFLICTS_PATH = '/booking-management/berth-conflicts';

/** A mediation case opens on its booking, where the mediation panel lives. */
export const getBerthConflictCasePath = (
  bookingId: string,
  mediationCaseId: string
) => `/booking-management/${bookingId}?mediationCaseId=${mediationCaseId}`;
